import React, { useEffect, useRef, useState } from 'react';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '../ui/dialog';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { toast } from 'sonner';
import api from '../../lib/api';
import { buildRegistrationAttributionPayload } from '../../lib/attribution';
import { trackRegistrationConfirmedOnce } from '../../lib/metrikaAnalytics';
import { trackVkRegistrationStart, trackVkRegistrationFormSubmitted, trackVkRegistrationConfirmed } from '../../lib/vkPixel';
import { classifyRequestError, trackFirstPartyEvent, trackFirstPartyEventOnce, trackFrontendError } from '../../lib/firstPartyAnalytics';
import { collectRegistrationErrors, fieldIsComplete, REGISTRATION_FIELDS, VALIDATION_MESSAGES } from '../../lib/registrationFormTelemetry';

const createRegistrationAttemptId = () => (typeof crypto !== 'undefined' && crypto.randomUUID
  ? crypto.randomUUID()
  : `attempt_${Date.now()}_${Math.random().toString(16).slice(2)}`);

const safeApiError = (error, fallback) => {
  const detail = error?.response?.data?.detail;
  if (typeof detail === 'string' && detail.trim()) return detail;
  if (Array.isArray(detail)) {
    const messages = detail.map((item) => (typeof item?.msg === 'string' ? item.msg : null)).filter(Boolean);
    if (messages.length) return messages.join('. ');
  }
  return fallback;
};

const fieldFromTarget = (target) => {
  const fieldName = target?.dataset?.registrationField;
  return REGISTRATION_FIELDS.includes(fieldName) ? fieldName : null;
};

const RegisterModal = ({ isOpen, onClose, onSwitchToLogin }) => {
  const [step, setStep] = useState('details');
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [age, setAge] = useState('');
  const [ageConfirmed, setAgeConfirmed] = useState(true);
  const [verificationCode, setVerificationCode] = useState('');
  const [loading, setLoading] = useState(false);
  const [validationErrors, setValidationErrors] = useState({});
  const attemptId = useRef(createRegistrationAttemptId());
  const checkboxRef = useRef(null);
  const submitRef = useRef(null);
  const refs = { name: useRef(null), email: useRef(null), password: useRef(null), age: useRef(null), age_checkbox: checkboxRef };

  useEffect(() => {
    if (isOpen) trackFirstPartyEventOnce('registration_form_view', attemptId.current);
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen || step !== 'details' || typeof window.IntersectionObserver !== 'function' || !submitRef.current) return undefined;
    const observer = new window.IntersectionObserver(([entry]) => {
      if (!entry?.isIntersecting || entry.intersectionRatio < 0.5) return;
      trackFirstPartyEventOnce('submit_visible', `${attemptId.current}:submit-visible`);
      observer.disconnect();
    }, { threshold: [0.5] });
    observer.observe(submitRef.current);
    return () => observer.disconnect();
  }, [isOpen, step]);

  const clearError = (fieldName) => setValidationErrors((current) => {
    if (!current[fieldName]) return current;
    const next = { ...current };
    delete next[fieldName];
    return next;
  });

  const completeRegistration = async (response) => {
    const user = response.data.user;
    const conversionId = response.data.registration_conversion_id || response.data.vk_registration_marker || user?.id || attemptId.current;
    localStorage.setItem('token', response.data.access_token);
    localStorage.setItem('user', JSON.stringify(user));
    trackRegistrationConfirmedOnce({ registrationConversionId: conversionId, userId: user?.id });
    trackFirstPartyEventOnce('registration_confirmed', conversionId);
    trackVkRegistrationConfirmed(conversionId);

    // This call enriches VK attribution only. Finish it before redirecting so navigation does not
    // abort a valid request and create a false frontend network error.
    if (response.data.vk_registration_marker) {
      try {
        await api.post('/auth/vk-registration-confirmed', { marker: response.data.vk_registration_marker });
      } catch (error) {
        trackFrontendError({ errorType: classifyRequestError(error), component: 'RegisterModal', errorCode: 'vk_marker_request_failed' });
      }
    }

    onClose();
    window.location.href = '/complete-profile';
  };

  const onFocus = (event) => {
    const fieldName = fieldFromTarget(event.target);
    trackVkRegistrationStart(attemptId.current);
    trackFirstPartyEventOnce('registration_start', attemptId.current);
    if (fieldName) trackFirstPartyEventOnce('registration_field_focus', `${attemptId.current}:focus:${fieldName}`, { fieldName });
  };

  const onBlur = (event) => {
    const fieldName = fieldFromTarget(event.target);
    if (!fieldName) return;
    const value = fieldName === 'age_checkbox' ? event.target.checked : event.target.value;
    if (fieldIsComplete(fieldName, value)) {
      trackFirstPartyEventOnce('registration_field_complete', `${attemptId.current}:complete:${fieldName}`, { fieldName });
    }
  };

  const validate = () => {
    const errors = collectRegistrationErrors({ name, email, password, age, ageConfirmed });
    const next = {};
    errors.forEach((error) => {
      next[error.fieldName] = error.validationCode;
      trackFirstPartyEvent('registration_validation_error', { fieldName: error.fieldName, validationCode: error.validationCode });
    });
    setValidationErrors(next);
    if (!errors.length) return true;
    const first = refs[errors[0].fieldName]?.current;
    first?.scrollIntoView?.({ behavior: 'smooth', block: 'center' });
    first?.focus?.({ preventScroll: true });
    return false;
  };

  const submitRegistration = async (event) => {
    event.preventDefault();
    trackFirstPartyEvent('registration_submit_click');
    if (!validate()) return;
    trackFirstPartyEventOnce('registration_form_submitted', attemptId.current);
    setLoading(true);
    try {
      const response = await api.post('/auth/register', {
        email,
        name: name.trim(),
        password,
        age: Number(age),
        age_confirmed: ageConfirmed,
        registration_attempt_id: attemptId.current,
        utm: buildRegistrationAttributionPayload(),
      });
      if (response.data.requires_verification) {
        trackVkRegistrationFormSubmitted(attemptId.current);
        trackFirstPartyEventOnce('email_code_sent', attemptId.current);
        toast.success('Код подтверждения отправлен на вашу почту');
        setStep('verification');
      } else if (response.data.access_token) {
        await completeRegistration(response);
      }
    } catch (error) {
      trackFrontendError({ errorType: classifyRequestError(error), component: 'RegisterModal', errorCode: 'registration_request_failed' });
      toast.error(safeApiError(error, 'Ошибка регистрации'));
    } finally {
      setLoading(false);
    }
  };

  const verifyCode = async (event) => {
    event.preventDefault();
    setLoading(true);
    try {
      await completeRegistration(await api.post('/auth/verify-email', { email, code: verificationCode }));
    } catch (error) {
      trackFrontendError({ errorType: classifyRequestError(error), component: 'RegisterModal', errorCode: 'verification_request_failed' });
      toast.error(safeApiError(error, 'Неверный код подтверждения'));
    } finally {
      setLoading(false);
    }
  };

  const close = () => {
    setStep('details');
    setVerificationCode('');
    setValidationErrors({});
    attemptId.current = createRegistrationAttemptId();
    onClose();
  };
  const errorText = (fieldName) => validationErrors[fieldName] && VALIDATION_MESSAGES[validationErrors[fieldName]];

  return (
    <Dialog open={isOpen} onOpenChange={close}>
      <DialogContent translate="no" data-testid="register-modal" className="notranslate max-h-[100dvh] w-[calc(100%_-_1rem)] max-w-md overflow-y-auto overscroll-contain px-5 pt-[max(1.25rem,env(safe-area-inset-top))] pb-[max(1.25rem,env(safe-area-inset-bottom))] sm:px-6" style={{ WebkitOverflowScrolling: 'touch' }}>
        <DialogHeader>
          <DialogTitle className="text-center text-2xl font-semibold">{step === 'verification' ? 'Подтверждение email' : 'Создайте аккаунт'}</DialogTitle>
          <DialogDescription className="text-center text-[#7A7A7A]">{step === 'verification' ? `Введите код из письма, отправленного на ${email}` : 'Это займёт меньше минуты. Укажите возраст — он нужен для подбора собеседника.'}</DialogDescription>
        </DialogHeader>

        <div hidden={step !== 'details'} aria-hidden={step !== 'details'}>
          <form noValidate onSubmit={submitRegistration} onFocusCapture={onFocus} onBlurCapture={onBlur} className="mt-3 space-y-4">
            <div><Label htmlFor="register-name">Имя</Label><Input ref={refs.name} id="register-name" data-registration-field="name" value={name} onChange={(event) => { setName(event.target.value); clearError('name'); }} autoComplete="name" aria-invalid={Boolean(errorText('name'))} />{errorText('name') && <p className="mt-1 text-sm font-medium text-red-600" role="alert">{errorText('name')}</p>}</div>
            <div><Label htmlFor="register-email">Email для получения кода регистрации</Label><Input ref={refs.email} id="register-email" data-registration-field="email" type="email" value={email} onChange={(event) => { setEmail(event.target.value); clearError('email'); }} autoComplete="email" inputMode="email" aria-invalid={Boolean(errorText('email'))} />{errorText('email') && <p className="mt-1 text-sm font-medium text-red-600" role="alert">{errorText('email')}</p>}</div>
            <div><Label htmlFor="register-password">Придумайте пароль для входа</Label><Input ref={refs.password} id="register-password" data-registration-field="password" type="password" value={password} onChange={(event) => { setPassword(event.target.value); clearError('password'); }} autoComplete="new-password" aria-invalid={Boolean(errorText('password'))} /><p className="mt-1 text-xs text-[#7A7A7A]">Минимум 6 символов</p>{errorText('password') && <p className="mt-1 text-sm font-medium text-red-600" role="alert">{errorText('password')}</p>}</div>
            <div className={`w-full rounded-xl border-2 p-3 ${errorText('age_checkbox') ? 'border-red-500 bg-red-50' : 'border-[#FF8B8B] bg-[#FFF7F7]'}`}>
              <label className="flex cursor-pointer items-center gap-3 font-medium" htmlFor="age-confirm"><input ref={checkboxRef} id="age-confirm" data-registration-field="age_checkbox" type="checkbox" checked={ageConfirmed} onChange={(event) => { setAgeConfirmed(event.target.checked); clearError('age_checkbox'); if (event.target.checked) { trackFirstPartyEventOnce('age_checkbox_checked', `${attemptId.current}:age-checkbox-checked`, { fieldName: 'age_checkbox' }); trackFirstPartyEventOnce('registration_field_complete', `${attemptId.current}:complete:age_checkbox`, { fieldName: 'age_checkbox' }); } }} className="h-6 w-6 shrink-0" /><span>Мне уже исполнилось 18 лет</span></label>
              {errorText('age_checkbox') && <p className="mt-2 text-sm font-medium text-red-600" role="alert">{errorText('age_checkbox')}</p>}
            </div>
            <div><Label htmlFor="register-age">Возраст</Label><Input ref={refs.age} id="register-age" data-registration-field="age" type="number" min="18" max="120" step="1" required value={age} onChange={(event) => { setAge(event.target.value); clearError('age'); }} inputMode="numeric" autoComplete="off" aria-invalid={Boolean(errorText('age'))} />{errorText('age') && <p className="mt-1 text-sm font-medium text-red-600" role="alert">{errorText('age')}</p>}</div>
            <div ref={submitRef} className="sticky bottom-0 z-10 -mx-1 bg-white/95 px-1 pb-[env(safe-area-inset-bottom)] pt-2 backdrop-blur"><Button type="submit" disabled={loading} className="w-full rounded-full py-6 text-lg font-semibold text-white" style={{ background: 'linear-gradient(135deg, #FF5757 0%, #FF7B7B 100%)' }} data-testid="register-submit-button">{loading ? 'Отправляем код…' : 'Получить код на email'}</Button></div>
            <div className="text-center text-sm text-[#7A7A7A]">Уже есть аккаунт? <button type="button" onClick={onSwitchToLogin} className="text-[#1A73E8] hover:underline">Войти</button></div>
          </form>
        </div>

        <div hidden={step !== 'verification'} aria-hidden={step !== 'verification'}>
          <form onSubmit={verifyCode} className="mt-4 space-y-4"><div><Label htmlFor="verification-code">Код подтверждения</Label><Input id="verification-code" value={verificationCode} onChange={(event) => setVerificationCode(event.target.value.replace(/\D/g, '').slice(0, 6))} inputMode="numeric" autoComplete="one-time-code" maxLength={6} className="text-center text-2xl tracking-widest" /></div><Button type="submit" disabled={loading || verificationCode.length !== 6} className="w-full rounded-full py-6 text-lg font-semibold">Подтвердить и продолжить</Button><button type="button" onClick={() => setStep('details')} className="w-full text-center text-sm font-medium text-[#1A73E8] hover:underline">Изменить данные</button></form>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default RegisterModal;
